import type { AxiosInstance, CreateAxiosDefaults } from 'axios';
import axios from 'axios';

import type { ErrFn, Interceptors, ReqFulfilledFn, ResFulfilledFn } from './type';

export class AxiosService {
  public readonly instance: AxiosInstance;
  private requestInterceptors: number | undefined;
  private responseInterceptors: number | undefined;

  constructor({ interceptors, ...config }: CreateAxiosDefaults & { interceptors?: Interceptors }) {
    this.instance = axios.create(config);

    this.handleInterceptors(interceptors);
  }

  setAccessToken(accessToken?: string) {
    this.instance.defaults.headers.common['Authorization'] = accessToken
      ? `Bearer ${accessToken}`
      : null;
  }

  ejectResInterceptor() {
    return () =>
      this.responseInterceptors &&
      this.instance.interceptors.response.eject(this.responseInterceptors);
  }

  ejectReqInterceptor() {
    return () =>
      this.requestInterceptors &&
      this.instance.interceptors.request.eject(this.requestInterceptors);
  }

  /**
   * Private Methods for Interceptors
   */

  private handleInterceptors(interceptors?: Interceptors) {
    if (!interceptors) return;

    const { req, res } = interceptors;

    if (req) {
      const {
        onError: error = (error) => Promise.reject(error),
        onFulfilled: fulfilled = (config) => Promise.resolve(config),
        middlewares,
      } = req;

      if (middlewares) {
        this.requestInterceptors = this.setReqInterceptors(
          [...middlewares].reverse().reduce((acc, middleware) => middleware(acc), fulfilled),
          error
        );
      }
    }

    if (res) {
      const {
        onError: error = (error) => Promise.reject(error),
        onFulfilled: fulfilled = (config) => Promise.resolve(config),
        middlewares,
      } = res;

      if (middlewares) {
        this.responseInterceptors = this.setResInterceptors(
          [...middlewares].reverse().reduce((acc, middleware) => middleware(acc), fulfilled),
          error
        );
      }
    }
  }

  private setReqInterceptors(onFulfilled: ReqFulfilledFn, onError: ErrFn) {
    return this.instance.interceptors.request.use(onFulfilled, onError);
  }

  private setResInterceptors(onFulfilled: ResFulfilledFn, onError: ErrFn) {
    return this.instance.interceptors.response.use(onFulfilled, onError);
  }
}
